<!-- ModalReportePedidos.vue — Reporte visual para captura de pantalla -->
<template>
  <Teleport to="body">
    <Transition name="reporte-fade">
      <div v-if="visible" class="rp-overlay" @click.self="cerrar">
        <div class="rp-modal">
          <!-- ── Controles (no se captura) ───────────────────── -->
          <div class="rp-controls">
            <div class="rp-controls-top">
              <h5 class="rp-titulo">Reporte de pedidos</h5>
              <button class="rp-cerrar" @click="cerrar">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
            <div class="rp-controls-row">
              <!-- Toggle píldora -->
              <div class="rp-toggle">
                <button
                  class="rp-toggle-btn"
                  :class="{ active: tipo === 'pendientes' }"
                  @click="tipo = 'pendientes'"
                >
                  Pendientes
                </button>
                <button
                  class="rp-toggle-btn"
                  :class="{ active: tipo === 'entregados' }"
                  @click="tipo = 'entregados'"
                >
                  Entregados
                </button>
              </div>
              <!-- Fechas -->
              <div class="rp-fechas">
                <input
                  type="date"
                  v-model="fechaDesde"
                  class="rp-input-fecha"
                  :title="tipo === 'entregados' ? 'Entrega desde' : 'Recibo desde'"
                />
                <span class="rp-sep">—</span>
                <input
                  type="date"
                  v-model="fechaHasta"
                  class="rp-input-fecha"
                  :title="tipo === 'entregados' ? 'Entrega hasta' : 'Recibo hasta'"
                />
              </div>
            </div>
          </div>

          <!-- ── Cuerpo capturabble ───────────────────────────── -->
          <div class="rp-body">
            <!-- Encabezado del reporte -->
            <div class="rp-print-header">
              <span class="rp-print-titulo">
                {{ tipo === "entregados" ? "Pedidos Entregados" : "Pedidos Pendientes" }}
              </span>
              <span class="rp-print-meta">
                <span v-if="fechaDesde || fechaHasta" class="rp-rango">
                  {{ fechaDesde || "—" }} / {{ fechaHasta || "—" }}
                </span>
                <span class="rp-fecha-hoy">{{ fechaHoyFormateada }}</span>
              </span>
            </div>

            <!-- Sin resultados -->
            <div v-if="pedidosFiltrados.length === 0" class="rp-empty">
              <i class="bi bi-inbox"></i>
              <p>Sin pedidos en este rango</p>
            </div>

            <!-- Grid de cards -->
            <div v-else class="rp-grid">
              <!-- CARD PENDIENTE -->
              <template v-if="tipo === 'pendientes'">
                <div v-for="p in pedidosFiltrados" :key="p.numero_pedido" class="rp-card">
                  <div class="rp-card-row1">
                    <span class="rp-card-num">#{{ p.numero_pedido }}</span>
                    <span class="rp-card-sep">|</span>
                    <span class="rp-card-fecha">
                      {{
                        p.fecha_compromiso
                          ? formatFechaCorta(p.fecha_compromiso)
                          : "Sin fecha"
                      }}
                    </span>
                    <span class="rp-card-sep">|</span>
                    <span :class="clsDias(p.dias_restantes)">{{
                      labelDias(p.dias_restantes)
                    }}</span>
                    <span class="rp-card-sep">|</span>
                    <span class="rp-card-piezas">
                      {{ p.total_articulos ?? "—" }}
                      <span class="rp-card-total-piezas">/ {{ p.total ?? "—" }}</span>
                    </span>
                  </div>
                  <div class="rp-card-row2">{{ formatNombre(p.cliente) }}</div>
                </div>
              </template>

              <!-- CARD ENTREGADO -->
              <template v-else>
                <div v-for="p in pedidosFiltrados" :key="p.numero_pedido" class="rp-card">
                  <div class="rp-card-row1">
                    <span class="rp-card-num">#{{ p.numero_pedido }}</span>
                    <span class="rp-card-sep">|</span>
                    <span class="rp-card-clicode">{{ p.cliente_numero }}</span>
                    <span class="rp-card-sep">|</span>
                    <span :class="clsEntrega(p.estatus_cliente)">
                      {{ p.estatus_cliente || "—" }}
                    </span>
                  </div>
                  <div class="rp-card-row2">{{ formatNombre(p.cliente) }}</div>
                </div>
              </template>
            </div>

            <!-- Total -->
            <p v-if="pedidosFiltrados.length > 0" class="rp-total">
              {{ pedidosFiltrados.length }} pedidos
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed } from "vue";

// ── Props & Emits ─────────────────────────────────────────
const props = defineProps({
  pedidos: { type: Array, default: () => [] },
});
const emit = defineEmits(["close"]);

// ── Estado interno ────────────────────────────────────────
const visible = ref(false);
const tipo = ref("pendientes");

const hoy = new Date();
const pad = (n) => String(n).padStart(2, "0");
const fechaDesde = ref(`${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-01`);
const fechaHasta = ref(
  `${hoy.getFullYear()}-${pad(hoy.getMonth() + 1)}-${pad(hoy.getDate())}`
);

// ── API pública ───────────────────────────────────────────
function abrir() {
  visible.value = true;
}
function cerrar() {
  visible.value = false;
  emit("close");
}
defineExpose({ abrir, cerrar });

// ── Computed ──────────────────────────────────────────────
const fechaHoyFormateada = computed(() =>
  new Date().toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
);

const pedidosFiltrados = computed(() => {
  return props.pedidos.filter((p) => {
    if (tipo.value === "entregados") {
      if (p.estatus_almacen !== "Entregado") return false;
      const fecha = p.fecha_entrega_real || p.fecha_recibo;
      if (fechaDesde.value && fecha < fechaDesde.value) return false;
      if (fechaHasta.value && fecha > fechaHasta.value) return false;
    } else {
      if (p.estatus_almacen === "Entregado" || p.estatus_almacen === "Cancelado")
        return false;
      if (fechaDesde.value && p.fecha_recibo < fechaDesde.value) return false;
      if (fechaHasta.value && p.fecha_recibo > fechaHasta.value) return false;
    }
    return true;
  });
});

// ── Helpers ───────────────────────────────────────────────
function formatNombre(nombre) {
  if (!nombre) return "";
  return nombre.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFechaCorta(fecha) {
  if (!fecha) return "—";
  const [, m, d] = fecha.split("-");
  const meses = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${parseInt(d)} ${meses[parseInt(m) - 1]}`;
}

function labelDias(dias) {
  if (dias === null || dias === undefined) return "Sin fecha";
  if (dias === 0) return "Vence hoy";
  if (dias > 0) return `${dias}d restantes`;
  return `${Math.abs(dias)}d atraso`;
}

function clsDias(dias) {
  if (dias === null || dias === undefined) return "rp-tag rp-gris";
  if (dias === 0) return "rp-tag rp-naranja";
  if (dias > 0) return "rp-tag rp-verde";
  return "rp-tag rp-rojo";
}

function clsEntrega(estatus) {
  const mapa = {
    "Entrega en tiempo": "rp-tag rp-verde",
    "Entrega con atraso": "rp-tag rp-rojo",
    Atrasado: "rp-tag rp-rojo",
    "En proceso": "rp-tag rp-azul",
  };
  return mapa[estatus] || "rp-tag rp-gris";
}
</script>

<style scoped>
/* ── Transición ── */
.reporte-fade-enter-active,
.reporte-fade-leave-active {
  transition: opacity 0.2s;
}
.reporte-fade-enter-from,
.reporte-fade-leave-to {
  opacity: 0;
}

/* ── Overlay ── */
.rp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 72px 12px 16px; /* 72px para dejar espacio al navbar */
  overflow-y: auto;
}

/* ── Modal ── */
.rp-modal {
  background: var(--d-cream);
  border-radius: 12px;
  width: 100%;
  max-width: 860px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

/* ══ CONTROLES ══════════════════════════════════════════════ */
.rp-controls {
  background: var(--d-cream);
  padding: 14px 18px 12px;
  border-bottom: 1px solid rgba(64, 34, 8, 0.15);
}
.rp-controls-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.rp-titulo {
  color: var(--d-brown);
  font-weight: 800;
  font-size: 0.95rem;
  margin: 0;
}
.rp-cerrar {
  background: none;
  border: none;
  color: var(--d-brown);
  opacity: 0.45;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  line-height: 1;
}
.rp-cerrar:hover {
  opacity: 1;
  background: rgba(64, 34, 8, 0.08);
}
.rp-controls-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

/* Toggle píldora */
.rp-toggle {
  display: flex;
  background: rgba(64, 34, 8, 0.08);
  border-radius: 20px;
  padding: 3px;
  flex-shrink: 0;
}
.rp-toggle-btn {
  padding: 4px 13px;
  border-radius: 18px;
  border: none;
  background: transparent;
  color: var(--d-brown);
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  opacity: 0.45;
  transition: all 0.15s;
  white-space: nowrap;
}
.rp-toggle-btn.active {
  background: var(--d-brown);
  color: var(--d-cream);
  opacity: 1;
}

/* Fechas */
.rp-fechas {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}
.rp-input-fecha {
  flex: 1;
  min-width: 0;
  padding: 5px 8px;
  border-radius: 6px;
  border: 1px solid rgba(64, 34, 8, 0.2);
  background: #ffffff;
  color: var(--d-brown);
  font-size: 0.74rem;
}
.rp-input-fecha:focus {
  outline: none;
  border-color: var(--d-brown);
}
.rp-sep {
  color: var(--d-brown);
  opacity: 0.35;
  font-weight: 700;
  flex-shrink: 0;
}

/* ══ BODY CAPTURABBLE ════════════════════════════════════════ */
.rp-body {
  background: var(--d-cream);
  padding: 14px 16px 18px;
}

/* Encabezado del reporte */
.rp-print-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
  padding-bottom: 7px;
  border-bottom: 2px solid var(--d-brown);
}
.rp-print-titulo {
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--d-brown);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.rp-print-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rp-rango {
  font-size: 0.68rem;
  color: #bc6c25;
  font-weight: 600;
}
.rp-fecha-hoy {
  font-size: 0.68rem;
  color: #bc6c25;
  font-weight: 700;
}

/* ══ GRID DE CARDS ═══════════════════════════════════════════ */
.rp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}

/* Card */
.rp-card {
  background: #ffffff;
  border: 1px solid rgba(64, 34, 8, 0.15);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Renglón 1 — datos clave */
.rp-card-row1 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--d-brown);
  line-height: 1.2;
}
.rp-card-num {
  font-weight: 800;
  color: var(--d-brown);
  white-space: nowrap;
}
.rp-card-sep {
  color: var(--d-brown);
  opacity: 0.25;
  font-weight: 400;
}
.rp-card-fecha {
  color: var(--d-brown);
  white-space: nowrap;
}
.rp-card-clicode {
  font-family: var(--d-font-mono);
  font-size: 0.68rem;
  color: #bc6c25;
  font-weight: 700;
}
.rp-card-piezas {
  white-space: nowrap;
  color: var(--d-brown);
}
.rp-card-total-piezas {
  opacity: 0.45;
  font-weight: 600;
}

/* Renglón 2 — nombre cliente */
.rp-card-row2 {
  font-size: 0.72rem;
  color: var(--d-brown);
  opacity: 0.75;
  line-height: 1.3;
  /* limitar a 2 líneas con ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Tags de estado */
.rp-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.62rem;
  font-weight: 800;
  white-space: nowrap;
}
.rp-verde {
  background: #d4edda;
  color: #155724;
}
.rp-rojo {
  background: #f8d7da;
  color: #721c24;
}
.rp-naranja {
  background: #fde8cc;
  color: #7d3c00;
}
.rp-azul {
  background: #cce5ff;
  color: #004085;
}
.rp-gris {
  background: #e2e3e5;
  color: #383d41;
}

/* Sin resultados */
.rp-empty {
  text-align: center;
  padding: 36px 0;
  color: var(--d-brown);
  opacity: 0.28;
}
.rp-empty i {
  font-size: 1.8rem;
  display: block;
  margin-bottom: 6px;
}
.rp-empty p {
  font-weight: 700;
  font-size: 0.82rem;
}

/* Total */
.rp-total {
  text-align: right;
  font-size: 0.7rem;
  color: #bc6c25;
  font-weight: 700;
  margin: 10px 0 0;
}

/* ══ RESPONSIVE ══════════════════════════════════════════════ */
@media (max-width: 600px) {
  .rp-overlay {
    padding: 64px 8px 12px;
  }
  .rp-grid {
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .rp-card {
    padding: 7px 8px;
  }
  .rp-card-row1 {
    font-size: 0.65rem;
  }
  .rp-card-row2 {
    font-size: 0.67rem;
  }
  .rp-controls-row {
    flex-wrap: wrap;
    gap: 8px;
  }
  .rp-fechas {
    flex: 1 0 100%;
  }
}
</style>
